// @ts-check

// Vendor imports
const Cookies = require('js-cookie')
const _ = require('lodash')
const qs = require('qs')
const { v5: uuid } = require('uuid')
// Client lib imports
const businessEvents = require('client/lib/business-events')
const cookieLib = require('client/lib/cookie')

// Type definitions

/** @typedef {(string|number)} UserId */
/** @typedef {string} SessionId */

/**
 * @typedef {Object} User
 * @property {UserId} userId
 * @property {boolean} loggedIn
 * @property {boolean} bot
 */

/**
 * @typedef {string} QueryString
 * @see https://tools.ietf.org/html/rfc3986#section-3.4
 */

/**
 * @typedef {Object.<string,any>} QueryParams
 * @see https://github.com/ljharb/qs#parsing-objects
 */

/** @typedef { import("./business-events/types.d").UtmParams } UtmParams */

// Constants
const CLIENT_UUID_NAMESPACE = '10155153-96ee-4699-881f-b088e79c1723'
const SESSION_EXPIRE_DURATION = 30 * 60 * 1000 // 30 min
const SESSION_COOKIE_PREFIX = 'usr_sess:'
const UTM_PREFIX = 'utm_'
const UTM_CUSTOM_PREFIX = UTM_PREFIX + 'mix_'

/**
 * Manages the session cookies associated with a {@link User}.
 * @class
 */
module.exports = class Session {
  /**
   * @param {User} user
   * @constructor
   */
  constructor(user) {
    /** @type {UserId} */
    this.userId = _.get(user, 'id')
    /** @type {boolean} */
    this.isLoggedIn = _.get(user, 'loggedIn', false)
    /** @type {boolean} */
    this.isBot = _.get(user, 'bot', false)
    /** @type {SessionId|void} */
    this.initialTempId = Cookies.get('temp_id')
    /** @type {(QueryString|void)} */
    this.cachedQueryString = undefined
    /** @type {(UtmParams|void)} */
    this.cachedUtmParams = undefined
  }

  /**
   * Update the persisted session ID for the current user.
   * This will refresh the `ttl` on the current session if it exists.
   * @returns {SessionId} The session ID for the current user.
   * @public
   */
  update() {
    if (this.isBot) {
      return
    }

    const tempId = Cookies.get('temp_id')
    const userSessionId = getSessionId(this.userId)
    const tempSessionId = tempId && getSessionId(tempId)

    this.maybeClearTempId(tempId)
    // The current session (if any) will get renewed immediately after clearing
    clearAllSessionCookies()

    const currentSessionId = userSessionId || tempSessionId || uuid(`${this.userId}`, CLIENT_UUID_NAMESPACE)
    const sessionPersisted = setSessionId(this.userId, currentSessionId)

    /**
     * Don't bother sending tracking if the session did not persist (i.e. cookies disabled),
     * as the session will effectively change every time {@link Session#update} is called.
     */
    if (sessionPersisted) {
      /**
       * Parse and cache the current UTM parameters so that subsequent calls to {@link Session#update}
       * don't try to call {@link Session#trackSessionUpdate} while {@link Session#trackSessionChanged}
       * is happening asynchronously.
       */
      const utmParams = this.getNewUtmParams()
      /**
       * The session is considered "changed" if the user is now logged-in and
       * did not have a session ID for their logged-in user ID, but now does.
       */
      if (this.isLoggedIn && !userSessionId) {
        this.trackSessionChanged(currentSessionId, utmParams)
      } else {
        this.trackSessionUpdate(currentSessionId, utmParams)
      }
    }

    return currentSessionId
  }

  /**
   * Add an event listener to check if the session has changed in another window.
   * @param {function} sessionChangedCallback Called when the current session ID changes.
   * @returns {function} Remove event listener and stop watching for session ID changes.
   * @public
   */
  watch(sessionChangedCallback) {
    if (this.isBot) {
      return _.noop
    }

    const { userId } = this
    // Capture the session at initial page load
    let mostRecentSessionId = getSessionId(userId)
    // @ts-ignore - jQuery is implicitly global
    $(window).on('focus blur', checkIfSessionHasChanged)
    return function () {
      // @ts-ignore - jQuery is implicitly global
      $(window).off('focus blur', checkIfSessionHasChanged)
    }

    function checkIfSessionHasChanged() {
      // Capture the session id on focus/blur
      const currentSessionId = getSessionId(userId)
      // The session may have been affected by other tabs or ttl timeout
      if (currentSessionId !== mostRecentSessionId) {
        sessionChangedCallback()
        mostRecentSessionId = currentSessionId
      }
    }
  }

  /**
   * Clears the anonymous user ID cookie once the user logs in.
   * Does not clear the cookie if it has changed in another tab.
   * @param {UserId} tempId
   * @private
   */
  maybeClearTempId(tempId) {
    if (this.isLoggedIn && tempId && tempId === this.initialTempId) {
      Cookies.remove('temp_id', cookieLib.clearAttributes('temp_id'))
    }
  }

  /**
   * Parses the current query string into {@link UtmParams}.
   * Caches the parsed result across multiple calls, and
   * returns `undefined` for non-existent or unchanged UTM parameters.
   * @returns {UtmParams|void} "New" UTM parameters or `undefined` if old/empty.
   * @private
   */
  getNewUtmParams() {
    const queryString = location.search
    if (!queryString || queryString === this.cachedQueryString) {
      return
    }
    this.cachedQueryString = queryString
    const utmParams = parseUtmParams(queryString)
    if (!utmParams || _.isEqual(utmParams, this.cachedUtmParams)) {
      return
    }
    this.cachedUtmParams = utmParams
    return utmParams
  }

  /**
   * Sends a `session-changed` tracking call,
   * followed by a `session-update` tracking call.
   * @param {SessionId} sessionId
   * @param {UtmParams|void} utmParams
   * @returns {Promise.<void>}
   * @private
   */
  async trackSessionChanged(sessionId, utmParams) {
    await Promise.all([
      businessEvents.trackProtobuf('TrackUserSession', {
        userId: this.userId,
        eventType: 'SESSION_STARTED',
        clientSession: {
          id: sessionId
        }
      }, {
        sessionId
      }),
      businessEvents.trackSegment('session-changed')
    ])
    await this.trackSessionUpdate(sessionId, utmParams)
  }

  /**
   * Send a `session-update` tracking call.
   * @param {SessionId} sessionId
   * @param {UtmParams|void} utmParams
   * @returns {Promise.<void>}
   * @private
   */
  async trackSessionUpdate(sessionId, utmParams) {
    if (!utmParams) {
      return
    }
    // Convert UtmParams into protobuf TrackingParams
    const trackingParams = _.transform(utmParams, (acc, value, key) => {
      acc[_.camelCase(key)] = value
    })
    await Promise.all([
      businessEvents.trackProtobuf('TrackUserSession', {
        userId: this.userId,
        eventType: 'SESSION_UPDATED',
        clientSession: {
          id: sessionId,
          track: trackingParams
        }
      }, {
        sessionId
      }),
      businessEvents.trackSegment('session-update')
    ])
  }
}

/**
 * Retrieve a session ID for a given user ID from the global cookie store.
 * Session cookies are considered expired after the `ttl`.
 * @param {UserId} userId
 * @returns {(SessionId|void)} The session ID or `undefined` if expired.
 */
function getSessionId(userId) {
  const session = Cookies.getJSON(SESSION_COOKIE_PREFIX + userId)
  const ttl = _.toSafeInteger(_.get(session, 'ttl'))
  if (ttl > Date.now()) {
    // Session is still valid
    return _.get(session, 'id')
  }
}

/**
 * Create or update a session cookie in the global cookie store.
 * @param {UserId} userId
 * @param {SessionId} sessionId
 * @returns {boolean} Whether the sessionId was successfully persisted.
 */
function setSessionId(userId, sessionId) {
  const name = SESSION_COOKIE_PREFIX + userId
  const ttl = Date.now() + SESSION_EXPIRE_DURATION
  const value = { id: sessionId, ttl }
  Cookies.set(name, value, cookieLib.setAttributes('usr_sess'))
  return getSessionId(userId) === sessionId
}

/**
 * Remove all session cookies from the global cookie store
 */
function clearAllSessionCookies() {
  for (let cookieName in Cookies.get()) {
    if (_.startsWith(cookieName, SESSION_COOKIE_PREFIX)) {
      Cookies.remove(cookieName, cookieLib.clearAttributes('usr_sess'))
    }
  }
}

/**
 * @param {QueryString} queryString A URI Query component string containing UTM parameters.
 * @returns {(UtmParams|void)} The parsed UTM parameters or `undefined` if there are none.
 */
function parseUtmParams(queryString) {
  /** @type {QueryParams} */
  const queryParams = qs.parse(queryString, {
    ignoreQueryPrefix: true
  })
  /** @type {QueryParams} */
  const customParams = {}
  /** @type {UtmParams} */
  const utmParams = _.transform(queryParams, (
    /** @type {UtmParams} */ obj,
    /** @type {any} */ value,
    /** @type {string} */ key
  ) => {
    if (!_.startsWith(key, UTM_PREFIX)) {
      // Ignore non-UTM query parameters
      return
    }
    if (_.startsWith(key, UTM_CUSTOM_PREFIX)) {
      const customKeySuffix = key.slice(UTM_CUSTOM_PREFIX.length)
      customParams[customKeySuffix] = value
    } else {
      // @ts-ignore - The startsWith check at runtime is good enough
      obj[key] = value
    }
  })
  if (!_.isEmpty(customParams)) {
    utmParams['utm_custom_params'] = customParams
  }
  return _.isEmpty(utmParams) ? null : utmParams
}
